import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonChip,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTextarea,
  IonToast,
  useIonAlert,
} from "@ionic/react";
import {
  addOutline,
  checkmarkCircle,
  contrastOutline,
  ellipseOutline,
  flagOutline,
  arrowBackOutline,
  checkmarkDoneOutline,
  copyOutline,
  checkboxOutline,
  createOutline,
  eyeOutline,
  listOutline,
  squareOutline,
  sparklesOutline,
  timeOutline,
  trashOutline,
} from "ionicons/icons";

import Header from "src/components/Header";
import RutinaVista, { EstadoRutinaChip, GARNET, numeroRutina } from "src/components/rutina/RutinaVista";
import RutinaAPI, {
  CatalogoTareas,
  ContextoPS,
  DIAS_SEMANA,
  Frecuencia,
  OperarioPS,
  PuntoServicio,
  ResumenPS,
  Rutina,
  RutinaPayload,
  etiquetaDias,
  mensajeError,
} from "src/services/rutina";

/**
 * Rutinas de limpieza (fiscales) - TD-11191.
 * Buscar PS -> elegir operario -> armar sus líneas en orden (franja horaria +
 * sector + tareas; las profundas se habilitan con un check por línea) ->
 * resumen -> confirmar (queda vigente). Si el horario difiere según el día, los
 * días con el mismo horario se agrupan y se arma una secuencia por grupo. Sin borradores: si no confirma, no se
 * guarda nada. Las tablas viven en EMSYS (Operaciones > Rutinas las consulta).
 */

type Vista = "panel" | "buscar" | "ps" | "editor" | "resumen" | "ver";

interface ConfigTarea {
  frecuencia: Frecuencia;
  dias_semana: number[];
  semanas_mes: number[];
}

type TipoLinea = "" | "TAREAS" | "PROFUNDA";

interface LineaEdit {
  key: string;
  /** Grupo de días (pestaña) al que pertenece la línea. */
  grupo: string;
  /** Días en que aplica: todos los del grupo o "solo algunos días". */
  dias: number[];
  desde: string;
  hasta: string;
  nombre: string;
  /** Una línea es de tareas o de limpieza profunda, nunca ambas: los checks
   *  "Agregar tarea" y "Limpieza profunda" son excluyentes. */
  tipo: TipoLinea;
  /** tarea_id -> configuración (las diarias van con frecuencia DIARIA). */
  tareas: Record<number, ConfigTarea>;
}

/** Días con la misma secuencia: arranca por los días con el mismo horario
 *  (ej. Lun–Vie 07:00-15:00) y el fiscal puede separar días con otra rutina. */
interface GrupoDias {
  key: string;
  dias: number[];
  etiqueta: string;
  cortes: [string, string][];
}

interface FormRutina {
  baltic_nro: string;
  observaciones: string;
  lineas: LineaEdit[];
}

interface Toast {
  show: boolean;
  msg: string;
  color?: string;
}

const MIN_CARACTERES_BUSQUEDA = 2;
/** A partir de cuántos operarios se muestra el buscador. */
const MIN_OPERARIOS_BUSCADOR = 5;

let contadorKeys = 0;
const nuevaKey = () => `l${Date.now()}_${contadorKeys++}`;

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

/** Ids de todos los cortes del operario (varias filas de AsignacionDet). */
const idsDe = (o: OperarioPS) => (o.asignacion_det_ids?.length ? o.asignacion_det_ids : [o.asignacion_det_id]);

/** Horario agrupado por día: [{dia: 'lun', tramos: ['07:00-11:00', '14:00-18:00']}]. */
const horarioPorDia = (o: OperarioPS) => {
  const porDia: { dia: string; tramos: string[] }[] = [];
  o.horario.forEach((h) => {
    const tramo = `${h.entrada}-${h.salida}`;
    const dia = porDia.find((d) => d.dia === h.dia);
    if (dia) {
      if (!dia.tramos.includes(tramo)) dia.tramos.push(tramo);
    } else porDia.push({ dia: h.dia, tramos: [tramo] });
  });
  return porDia;
};

/** Agrupa los días que tienen exactamente el mismo horario. Sin horario
 *  cargado: un único grupo con todos los días. */
const gruposDe = (o: OperarioPS): GrupoDias[] => {
  const porDia = new Map<number, [string, string][]>();
  o.horario.forEach((h) => {
    const dia = DIAS_SEMANA.find((d) => d.clave === h.dia)?.valor;
    if (!dia) return;
    const cortes = porDia.get(dia) || [];
    if (!cortes.some((c) => c[0] === h.entrada && c[1] === h.salida)) cortes.push([h.entrada, h.salida]);
    porDia.set(dia, cortes);
  });
  if (!porDia.size) return [{ key: "1,2,3,4,5,6,7", dias: [1, 2, 3, 4, 5, 6, 7], etiqueta: "Todos los días", cortes: [] }];
  const grupos: GrupoDias[] = [];
  Array.from(porDia.keys())
    .sort((a, b) => a - b)
    .forEach((dia) => {
      const cortes = porDia.get(dia)!.sort((a, b) => a[0].localeCompare(b[0]));
      const firma = cortes.map((c) => c.join("-")).join("/");
      const grupo = grupos.find((g) => g.cortes.map((c) => c.join("-")).join("/") === firma);
      if (grupo) grupo.dias.push(dia);
      else grupos.push({ key: "", dias: [dia], etiqueta: "", cortes });
    });
  return grupos.map((g) => ({ ...g, key: g.dias.join(","), etiqueta: etiquetaDias(g.dias) }));
};

/** Misma regla que el backend: la franja cae dentro de algún corte (contempla
 *  cortes que pasan la medianoche). */
const dentroDeCortes = (desde: string, hasta: string, cortes: [string, string][]) =>
  cortes.some(([ent, sal]) => {
    if (ent <= sal) return ent <= desde && hasta <= sal && desde < hasta;
    if (desde < hasta) return desde >= ent || hasta <= sal;
    return desde >= ent && hasta <= sal;
  });

const sumarMinutos = (hora: string, minutos: number) => {
  const [h, m] = hora.split(":").map(Number);
  const total = (((h * 60 + m + minutos) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const DURACION_LINEA = 60; // minutos por defecto de una línea nueva

const aMinutos = (hora: string) => {
  const [h, m] = hora.split(":").map(Number);
  return h * 60 + m;
};
const aHora = (minutos: number) => sumarMinutos("00:00", minutos);

/**
 * Franja sugerida para una línea nueva del grupo: arranca donde termina la
 * última línea cargada y respeta la asignación del operario. Si la anterior
 * terminó al final de un corte (jornada partida), salta a la entrada del corte
 * siguiente; el "hasta" no pasa de la salida del corte. Contempla cortes que
 * pasan la medianoche. null = el horario del grupo ya está cubierto.
 */
const siguienteFranja = (cortes: [string, string][], lineas: LineaEdit[]): { desde: string; hasta: string } | null => {
  if (!cortes.length) {
    const ultima = lineas.map((l) => l.hasta).filter(Boolean).sort().pop() || "07:00";
    return { desde: ultima, hasta: sumarMinutos(ultima, DURACION_LINEA) };
  }
  // Cortes en una línea de tiempo continua (la salida nocturna suma 24 h).
  const tramos = cortes
    .map(([ent, sal]) => {
      const e = aMinutos(ent);
      let x = aMinutos(sal);
      if (x <= e) x += 1440;
      return [e, x] as [number, number];
    })
    .sort((a, b) => a[0] - b[0]);
  const inicio = tramos[0][0];
  // Hora de una línea en esa misma línea de tiempo.
  const enTiempo = (hora: string) => {
    const m = aMinutos(hora);
    return m < inicio ? m + 1440 : m;
  };
  const finales = lineas.filter((l) => l.hasta).map((l) => enTiempo(l.hasta));
  const fin = finales.length ? Math.max(...finales) : inicio;
  const dentro = tramos.find(([e, x]) => fin >= e && fin < x);
  const siguiente = dentro || tramos.find(([e]) => e >= fin);
  if (!siguiente) return null;
  const desde = dentro ? fin : siguiente[0];
  const hasta = Math.min(desde + DURACION_LINEA, siguiente[1]);
  return { desde: aHora(desde), hasta: aHora(hasta) };
};

type EstadoTramo = "COMPLETO" | "PARCIAL" | "PENDIENTE";

interface LineaCobertura {
  dias: number[];
  desde: string;
  hasta: string;
}

/** Línea recién agregada que no se completó: no se valida ni se guarda. */
const lineaVacia = (l: LineaEdit) => !l.tipo && !l.nombre.trim() && !Object.keys(l.tareas).length;

/** Fecha 'YYYY-MM-DD[ HH:MM]' del backend como 'DD/MM/YYYY[ HH:MM]'. */
const fechaCorta = (valor?: string | null) => {
  if (!valor) return "";
  const [fecha, hora] = valor.split(" ");
  const [a, m, d] = fecha.split("-");
  return d && m && a ? `${d}/${m}/${a}${hora ? ` ${hora}` : ""}` : valor;
};

/** Líneas para medir cobertura (solo las que ya son válidas: con tipo, alguna
 *  tarea y la franja dentro del horario de su grupo): las de tareas por sus
 *  días; las de profunda por los días de sus limpiezas. */
const lineasCobertura = (lineas: LineaEdit[], grupos: GrupoDias[]): LineaCobertura[] =>
  lineas
    .filter(
      (l) =>
        !!l.tipo &&
        Object.keys(l.tareas).length > 0 &&
        franjaValida(l, grupos.find((g) => g.key === l.grupo)?.cortes || [])
    )
    .map((l) => ({
      desde: l.desde,
      hasta: l.hasta,
      dias:
        l.tipo === "PROFUNDA"
          ? Array.from(new Set(Object.values(l.tareas).flatMap((c) => c.dias_semana)))
          : l.dias,
    }));

/**
 * Cuánto de cada tramo del horario cubren las líneas (misma regla que
 * api-central, estado_cobertura): {día ISO: {"07:00-11:00": estado}}.
 */
const coberturaHorario = (op: OperarioPS, lineas: LineaCobertura[]) => {
  const salida: Record<number, Record<string, EstadoTramo>> = {};
  const porDia: Record<number, [string, string][]> = {};
  op.horario.forEach((h) => {
    const dia = DIAS_SEMANA.find((d) => d.clave === h.dia)?.valor;
    if (dia) (porDia[dia] = porDia[dia] || []).push([h.entrada, h.salida]);
  });
  Object.entries(porDia).forEach(([diaTxt, cortes]) => {
    const dia = Number(diaTxt);
    cortes.sort((a, b) => a[0].localeCompare(b[0]));
    const inicio = aMinutos(cortes[0][0]);
    const franja = (desde: string, hasta: string): [number, number] => {
      const enTiempo = (h: string) => (aMinutos(h) < inicio ? aMinutos(h) + 1440 : aMinutos(h));
      const d = enTiempo(desde);
      let h = enTiempo(hasta);
      if (h <= d) h += 1440;
      return [d, h];
    };
    const franjas = lineas
      .filter((l) => l.dias.includes(dia))
      .map((l) => franja(l.desde, l.hasta))
      .sort((a, b) => a[0] - b[0]);
    salida[dia] = {};
    cortes.forEach(([ent, sal]) => {
      const [cd, ch] = franja(ent, sal);
      let cubierto = 0;
      let cursor = cd;
      franjas.forEach(([d0, h0]) => {
        const d = Math.max(d0, cursor);
        const h = Math.min(h0, ch);
        if (h > d) {
          cubierto += h - d;
          cursor = h;
        }
      });
      salida[dia][`${ent}-${sal}`] = cubierto >= ch - cd ? "COMPLETO" : cubierto > 0 ? "PARCIAL" : "PENDIENTE";
    });
  });
  return salida;
};

const ICONO_TRAMO: Record<EstadoTramo, { icono: string; color: string; texto: string }> = {
  COMPLETO: { icono: checkmarkCircle, color: "#2e7d32", texto: "cubierto" },
  PARCIAL: { icono: contrastOutline, color: "#ef6c00", texto: "a medias" },
  PENDIENTE: { icono: ellipseOutline, color: "#9e9e9e", texto: "falta" },
};

const normalizar = (texto: string) =>
  (texto || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const filtrarOperarios = (lista: OperarioPS[], filtro: string) => {
  const f = normalizar(filtro);
  if (!f) return lista;
  return lista.filter((o) => normalizar(o.nombre).includes(f) || String(o.legajo).includes(f));
};

const toggle = (lista: number[], valor: number) =>
  lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor].sort((a, b) => a - b);

const describirFrecuencia = (c: ConfigTarea) => {
  const dias = c.dias_semana.map((d) => DIAS_SEMANA.find((x) => x.valor === d)?.largo || d).join(", ");
  if (c.frecuencia === "SEMANAL") return `Semanal: ${dias || "sin día"}`;
  const semanas = c.semanas_mes.map((s) => `${s}ª`).join(" y ");
  const etiqueta = c.frecuencia === "QUINCENAL" ? "Quincenal" : "Mensual";
  return `${etiqueta}: ${semanas || "sin semana"} semana, ${dias || "sin día"}`;
};

/** Ordena por grupo y, dentro de cada grupo, por hora (las de después de
 *  medianoche al final en turnos nocturnos). */
const ordenarLineas = (lineas: LineaEdit[], grupos: GrupoDias[]) => {
  const clave = (l: LineaEdit) => {
    const idx = grupos.findIndex((g) => g.key === l.grupo);
    const inicio = grupos[idx]?.cortes[0]?.[0] || "00:00";
    const desde = l.desde || "99:99";
    return `${String(idx).padStart(2, "0")}${desde < inicio ? 1 : 0}${desde}`;
  };
  return [...lineas].sort((a, b) => clave(a).localeCompare(clave(b)));
};

/** Ajusta una línea a otros días: las profundas conservan solo esos días. */
const conDias = (l: LineaEdit, dias: number[], grupo: string): LineaEdit => ({
  ...JSON.parse(JSON.stringify(l)),
  key: nuevaKey(),
  grupo,
  dias: [...dias].sort((a, b) => a - b),
  tareas: Object.fromEntries(
    Object.entries(l.tareas).map(([id, c]) => [
      id,
      c.frecuencia === "DIARIA" ? c : { ...c, dias_semana: c.dias_semana.filter((d) => dias.includes(d)) },
    ])
  ),
});

/** Línea de profunda sin las limpiezas que se quedaron sin días (null si no
 *  queda ninguna). Las de tareas no cambian. */
const sinProfundasVacias = (l: LineaEdit): LineaEdit | null => {
  if (l.tipo !== "PROFUNDA") return l;
  const tareas = Object.fromEntries(
    Object.entries(l.tareas).filter(([, c]) => c.dias_semana.length > 0)
  ) as Record<number, ConfigTarea>;
  return Object.keys(tareas).length ? { ...l, tareas } : null;
};

/** Copia una línea del grupo origen al grupo destino. Tareas: si aplicaba a
 *  todo el origen aplica a todo el destino; si era "solo algunos días" conserva
 *  los días en común (o todo el destino si no hay ninguno en común). Profunda:
 *  aplica a todo el destino y cada limpieza conserva solo los días en común
 *  (las que quedan sin día se copian igual, para elegirlo). */
const copiarAGrupo = (l: LineaEdit, origen: GrupoDias, destino: GrupoDias) => {
  if (l.tipo === "PROFUNDA") return conDias(l, destino.dias, destino.key);
  const completa = l.dias.length === origen.dias.length;
  const comunes = l.dias.filter((d) => destino.dias.includes(d));
  return conDias(l, completa || !comunes.length ? destino.dias : comunes, destino.key);
};

/** Reparte las líneas de una rutina guardada en los grupos del horario actual
 *  del operario (días con el mismo horario). Se descartan las líneas de días
 *  que ya no trabaja y las tareas dadas de baja en el catálogo (`vigentes`). */
const lineasDesdeRutina = (r: Rutina, grupos: GrupoDias[], vigentes: Set<number>) => {
  const lineas: LineaEdit[] = [];
  let descartadas = 0;
  let tareasDeBaja = 0;
  r.sectores.forEach((s) => {
    const tareas = s.tareas.filter((t) => vigentes.has(t.tarea_id));
    tareasDeBaja += s.tareas.length - tareas.length;
    if (!tareas.length) return;
    const base: LineaEdit = {
      key: "",
      grupo: "",
      dias: [],
      desde: s.hora_desde || "",
      hasta: s.hora_hasta || "",
      nombre: s.nombre,
      tipo: (s.tipo as TipoLinea) || (tareas.some((t) => t.tipo === "PROFUNDA") ? "PROFUNDA" : "TAREAS"),
      tareas: Object.fromEntries(
        tareas.map((t) => [
          t.tarea_id,
          { frecuencia: t.frecuencia, dias_semana: t.dias_semana, semanas_mes: t.semanas_mes },
        ])
      ),
    };
    const diasLinea = s.dias_semana?.length ? s.dias_semana : [1, 2, 3, 4, 5, 6, 7];
    let usada = false;
    grupos.forEach((g) => {
      const dias = diasLinea.filter((x) => g.dias.includes(x));
      if (!dias.length) return;
      // Una línea de profunda aplica a todo el grupo; sus días los dan las profundas.
      const linea = sinProfundasVacias(conDias(base, base.tipo === "PROFUNDA" ? g.dias : dias, g.key));
      if (!linea) return;
      usada = true;
      lineas.push(linea);
    });
    if (!usada) descartadas++;
  });
  return { lineas: ordenarLineas(lineas, grupos), descartadas, tareasDeBaja };
};

/** Minutos de la franja en la línea de tiempo del grupo (con turno nocturno,
 *  lo posterior a medianoche suma 24 h). */
const franjaEnMinutos = (l: LineaEdit, inicio: number): [number, number] => {
  const enTiempo = (hora: string) => {
    const m = aMinutos(hora);
    return m < inicio ? m + 1440 : m;
  };
  const desde = enTiempo(l.desde);
  let hasta = enTiempo(l.hasta);
  if (hasta <= desde) hasta += 1440;
  return [desde, hasta];
};

const TODAS_LAS_SEMANAS = [1, 2, 3, 4, 5];

/** "día-semana" que ocupa una línea (misma regla que api-central): las de
 *  tareas ocupan todas las semanas de sus días; las de profunda, los días y
 *  semanas de cada limpieza (dos mensuales de semanas distintas no se pisan). */
const ocupacion = (l: LineaEdit): Set<string> => {
  const salida = new Set<string>();
  if (l.tipo === "TAREAS") {
    l.dias.forEach((d) => TODAS_LAS_SEMANAS.forEach((s) => salida.add(`${d}-${s}`)));
    return salida;
  }
  Object.values(l.tareas).forEach((c) => {
    const semanas = c.frecuencia === "SEMANAL" ? TODAS_LAS_SEMANAS : c.semanas_mes;
    c.dias_semana.forEach((d) => semanas.forEach((s) => salida.add(`${d}-${s}`)));
  });
  return salida;
};

/** Días en común entre dos ocupaciones. */
const diasComunes = (a: Set<string>, b: Set<string>) =>
  Array.from(new Set(Array.from(a).filter((x) => b.has(x)).map((x) => Number(x.split("-")[0])))).sort((x, y) => x - y);

/** La franja es válida para evaluar choques: está dentro del horario del grupo
 *  (o, sin horario, desde < hasta). Mientras se edita una hora puede no serlo. */
const franjaValida = (l: LineaEdit, cortes: [string, string][]) =>
  !!l.desde && !!l.hasta && (cortes.length ? dentroDeCortes(l.desde, l.hasta, cortes) : l.desde < l.hasta);

interface Choque {
  linea: LineaEdit;
  dias: number[];
}

/**
 * Líneas del mismo grupo que ya ocupan algún día de la línea en una franja que
 * se superpone (aunque sea en parte), sean tareas o limpiezas profundas. Para
 * las profundas cuentan sus días y semanas del mes.
 */
const choquesDe = (l: LineaEdit, lineas: LineaEdit[], grupos: GrupoDias[]): Choque[] => {
  const grupo = grupos.find((g) => g.key === l.grupo);
  const cortes = grupo?.cortes || [];
  if (!l.tipo || !franjaValida(l, cortes)) return [];
  const inicio = aMinutos(cortes[0]?.[0] || "00:00");
  const [d1, h1] = franjaEnMinutos(l, inicio);
  const propia = ocupacion(l);
  return lineas
    .filter((o) => o.key !== l.key && o.grupo === l.grupo && !!o.tipo && franjaValida(o, cortes))
    .filter((o) => {
      const [d2, h2] = franjaEnMinutos(o, inicio);
      return d1 < h2 && d2 < h1;
    })
    .map((o) => ({ linea: o, dias: diasComunes(propia, ocupacion(o)) }))
    .filter((c) => c.dias.length > 0);
};

// ---------------------------------------------------------------------------

const Rutinas: React.FC = () => {
  const [presentAlert] = useIonAlert();
  const [vista, setVista] = useState<Vista>("buscar");
  const [toast, setToast] = useState<Toast>({ show: false, msg: "" });
  const [catalogo, setCatalogo] = useState<CatalogoTareas | null>(null);
  const [errorCatalogo, setErrorCatalogo] = useState(false);
  const history = useHistory();

  // Búsqueda de PS
  const [q, setQ] = useState("");
  const [puntos, setPuntos] = useState<PuntoServicio[]>([]);
  const [buscando, setBuscando] = useState(false);
  const timer = useRef<any>(null);
  const ultimaBusqueda = useRef("");

  // PS, operario y rutina
  const [ctx, setCtx] = useState<ContextoPS | null>(null);
  const [cargando, setCargando] = useState(false);
  const [operario, setOperario] = useState<OperarioPS | null>(null);
  /** Rutina vigente que se está viendo o modificando (null = nueva). */
  const [rutina, setRutina] = useState<Rutina | null>(null);
  const [form, setForm] = useState<FormRutina | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [filtroOp, setFiltroOp] = useState("");
  /** Grupo de días que se está editando. */
  const [grupoKey, setGrupoKey] = useState("");
  const [grupos, setGrupos] = useState<GrupoDias[]>([]);
  /** Fuerza a redibujar los campos de una línea cuando se cancela un cambio. */
  const [revLinea, setRevLinea] = useState(0);
  /** Avisos al abrir una rutina guardada (líneas o tareas que se quitaron). */
  const [avisoCarga, setAvisoCarga] = useState("");
  const [revCopiar, setRevCopiar] = useState(0);

  const grupoActivo = grupos.find((g) => g.key === grupoKey) || grupos[0];

  const error = (err: any, msg: string) =>
    setToast({ show: true, msg: mensajeError(err, msg), color: "danger" });
  const aviso = (msg: string) => setToast({ show: true, msg, color: "warning" });

  const catalogoTareas = catalogo ? [...catalogo.diarias, ...catalogo.profundas] : [];
  const nombreTarea = (id: number) => catalogoTareas.find((t) => t.id === id)?.nombre || `Tarea ${id}`;
  const esProfunda = (id: number) => !!catalogo?.profundas.some((t) => t.id === id);

  // Evita actualizar el estado si se salió de la pantalla antes de que respondan.
  const montado = useRef(true);
  useEffect(
    () => () => {
      montado.current = false;
    },
    []
  );

  const cargarCatalogo = useCallback(() => {
    setErrorCatalogo(false);
    RutinaAPI.catalogo()
      .then((r) => montado.current && setCatalogo(r.data.datos))
      .catch((err) => {
        if (!montado.current) return;
        setErrorCatalogo(true);
        setToast({ show: true, msg: mensajeError(err, "No se pudo cargar el catálogo de tareas"), color: "danger" });
      });
  }, []);

  useEffect(() => {
    cargarCatalogo();
  }, [cargarCatalogo]);

  // Jefe de Operaciones: arranca en el panel de avance de las rutinas de los PS
  // de sus fiscales; el fiscal, en el buscador de sus PS.
  const [esJefe, setEsJefe] = useState(false);
  /** Hasta saber el rol no se muestra ninguna vista (evita que el jefe vea un
   *  instante el buscador del fiscal). */
  const [rolCargado, setRolCargado] = useState(false);
  const [panel, setPanel] = useState<ResumenPS[] | null>(null);
  const [errorPanel, setErrorPanel] = useState(false);
  const [filtroPanel, setFiltroPanel] = useState("");
  /** silencioso: al volver de un PS se actualiza sin spinner ni perder el scroll. */
  const cargarPanel = useCallback((silencioso = false) => {
    if (!silencioso) setPanel(null);
    setErrorPanel(false);
    RutinaAPI.resumen()
      .then((r) => montado.current && setPanel(r.data.datos || []))
      .catch((err) => {
        if (!montado.current) return;
        setErrorPanel(true);
        setToast({ show: true, msg: mensajeError(err, "No se pudo cargar el avance de las rutinas"), color: "danger" });
      });
  }, []);
  useEffect(() => {
    RutinaAPI.rol()
      .then((r) => {
        if (!montado.current) return;
        if (r.data.datos.jefe) {
          setEsJefe(true);
          setVista((v) => (v === "buscar" ? "panel" : v));
          cargarPanel();
        }
      })
      .catch(() => {
        if (montado.current) aviso("No se pudo verificar tu perfil: se muestra el buscador de puntos de servicio");
      })
      .finally(() => montado.current && setRolCargado(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargarPanel]);

  // Búsqueda con demora: se cancela si se sale de la pantalla.
  useEffect(() => () => clearTimeout(timer.current), []);

  // Mientras se arma o revisa una rutina, salir por el menú o con el botón
  // atrás del celular pide confirmación (lo cargado no está guardado).
  const editando = vista === "editor" || vista === "resumen";
  useEffect(() => {
    if (!editando) return undefined;
    return history.block("Lo cargado en la rutina no se guarda. ¿Salir igual?");
  }, [editando, history]);

  // ---------------- búsqueda de PS ----------------

  const buscar = useCallback((texto: string) => {
    ultimaBusqueda.current = texto;
    setBuscando(true);
    RutinaAPI.puntosServicio(texto)
      .then((r) => {
        // Descarta respuestas de búsquedas anteriores que llegan tarde.
        if (ultimaBusqueda.current === texto) setPuntos(r.data.datos || []);
      })
      .catch((err) =>
        setToast({ show: true, msg: mensajeError(err, "No se pudieron buscar los puntos de servicio"), color: "danger" })
      )
      .finally(() => {
        if (ultimaBusqueda.current === texto) setBuscando(false);
      });
  }, []);

  /** Se busca a medida que el fiscal escribe (no se listan todos los PS). */
  const onBuscar = (texto: string) => {
    setQ(texto);
    clearTimeout(timer.current);
    const limpio = texto.trim();
    if (limpio.length < MIN_CARACTERES_BUSQUEDA) {
      ultimaBusqueda.current = "";
      setPuntos([]);
      setBuscando(false);
      return;
    }
    timer.current = setTimeout(() => buscar(limpio), 400);
  };

  const abrirPS = async (psId: number) => {
    if (cargando) return;
    setCargando(true);
    try {
      const r = await RutinaAPI.contextoPS(psId);
      setCtx(r.data.datos);
      setFiltroOp("");
      setVista("ps");
    } catch (err) {
      error(err, "No se pudo abrir el punto de servicio");
    } finally {
      setCargando(false);
    }
  };

  // ---------------- operario -> rutina ----------------

  /** Línea nueva del grupo: arranca en la entrada de esos días. */
  const lineaInicial = (g: GrupoDias, franja?: { desde: string; hasta: string } | null): LineaEdit => {
    const { desde, hasta } = franja || siguienteFranja(g.cortes, []) || { desde: "07:00", hasta: "08:00" };
    return { key: nuevaKey(), grupo: g.key, dias: [...g.dias], desde, hasta, nombre: "", tipo: "", tareas: {} };
  };

  const armarRutina = (op: OperarioPS) => {
    if (!ctx) return;
    const gs = gruposDe(op);
    setOperario(op);
    setRutina(null);
    setGrupos(gs);
    setGrupoKey(gs[0].key);
    setForm({ baltic_nro: ctx.baltics[0]?.relevamiento || "", observaciones: "", lineas: gs.map((g) => lineaInicial(g)) });
    setVista("editor");
  };

  const abrirRutina = async (id: number, op: OperarioPS | null, modo: Vista) => {
    if (cargando) return;
    setCargando(true);
    try {
      const r = await RutinaAPI.obtener(id);
      const datos = r.data.datos;
      const legajo = datos.asignaciones?.[0]?.legajo;
      const elegido = op || ctx?.operarios.find((o) => o.legajo === legajo) || null;
      const gs = elegido ? gruposDe(elegido) : [{ key: "1,2,3,4,5,6,7", dias: [1, 2, 3, 4, 5, 6, 7], etiqueta: "Todos los días", cortes: [] as [string, string][] }];
      const { lineas, descartadas, tareasDeBaja } = lineasDesdeRutina(datos, gs, idsCatalogo());
      setOperario(elegido);
      setRutina(datos);
      setGrupos(gs);
      setGrupoKey(gs[0]?.key || "");
      // Referencia Baltic: la guardada si sigue vigente; si no, la vigente del PS.
      const vigentes = ctx?.baltics || [];
      const baltic = vigentes.some((b) => b.relevamiento === datos.baltic_nro)
        ? datos.baltic_nro || ""
        : vigentes[0]?.relevamiento || datos.baltic_nro || "";
      setForm({ baltic_nro: baltic, observaciones: datos.observaciones || "", lineas });
      const avisos = [
        descartadas ? `${descartadas} línea(s) eran de días que el operario ya no trabaja` : "",
        tareasDeBaja ? `${tareasDeBaja} tarea(s) fueron dadas de baja del catálogo` : "",
      ].filter(Boolean);
      setAvisoCarga(avisos.length ? `Al modificar se quitan: ${avisos.join(" y ")}.` : "");
      if (modo === "editor") irAEditor(avisos.length ? `Al modificar se quitan: ${avisos.join(" y ")}.` : "");
      else setVista(modo);
    } catch (err) {
      error(err, "No se pudo abrir la rutina");
    } finally {
      setCargando(false);
    }
  };

  /** Pasa al editor mostrando, si hay, lo que se quitó al abrir la rutina. */
  const irAEditor = (mensaje: string = avisoCarga) => {
    if (mensaje) aviso(mensaje);
    setVista("editor");
  };

  const idsCatalogo = () => new Set(catalogoTareas.map((t) => t.id));

  /** Copia las líneas de la rutina de otro operario del PS. */
  const copiarDe = async (rutinaId: number) => {
    const aplicar = async () => {
      try {
        const r = await RutinaAPI.obtener(rutinaId);
        const { lineas } = lineasDesdeRutina(r.data.datos, grupos, idsCatalogo());
        setForm((f) => (f ? { ...f, lineas } : f));
        setToast({ show: true, msg: "Líneas copiadas: revisá los horarios", color: "success" });
      } catch (err) {
        error(err, "No se pudo copiar la rutina");
      }
    };
    // El selector vuelve a "Elegir" para poder volver a elegir la misma opción.
    setRevCopiar((r) => r + 1);
    const tieneContenido = form?.lineas.some((l) => l.nombre || Object.keys(l.tareas).length);
    if (!tieneContenido) return aplicar();
    presentAlert({
      header: "Copiar rutina",
      message: "Se reemplazan las líneas cargadas por las de la rutina elegida.",
      buttons: [
        { text: "Cancelar", role: "cancel" },
        { text: "Copiar", handler: () => { aplicar(); } },
      ],
    });
  };

  /** Finalizar: la rutina terminó y queda registrado su período (desde/hasta).
   *  Anular: se cargó por error. En ambos casos el operario deja de verla. */
  const darDeBaja = (r: Rutina, accion: "finalizar" | "anular") => {
    const finalizar = accion === "finalizar";
    presentAlert({
      header: finalizar ? "Finalizar rutina" : "Anular rutina",
      message: finalizar
        ? `La rutina queda finalizada hoy${r.inicio_en ? ` (vigente desde el ${fechaCorta(r.inicio_en)})` : ""} y el operario deja de verla.`
        : "Usalo solo si se cargó por error: no queda como período de vigencia. Si es una modificación, vuelve a regir la versión anterior.",
      inputs: [{ name: "motivo", type: "text", placeholder: finalizar ? "Motivo (opcional)" : "Motivo (obligatorio)" }],
      buttons: [
        { text: "Cancelar", role: "cancel" },
        {
          text: finalizar ? "Finalizar" : "Anular",
          handler: (data: any) => {
            if (!finalizar && !String(data?.motivo || "").trim()) {
              aviso("Indicá el motivo de la anulación");
              return false;
            }
            enviarBaja(r, finalizar, data?.motivo);
            return true;
          },
        },
      ],
    });
  };

  const enviarBaja = async (r: Rutina, finalizar: boolean, motivo?: string) => {
    if (!ctx) return;
    try {
      const resp = finalizar
        ? await RutinaAPI.finalizar(r.rutina_id, motivo)
        : await RutinaAPI.anular(r.rutina_id, motivo);
      const reactivada = (resp.data as any)?.datos?.reactivada;
      setToast({
        show: true,
        msg: finalizar
          ? "Rutina finalizada"
          : reactivada
          ? `Rutina anulada: vuelve a regir la versión ${reactivada}`
          : "Rutina anulada",
        color: "success",
      });
      // Ya dada de baja: se sale de "ver" antes de recargar el PS.
      setRutina(null);
      setVista("ps");
      await abrirPS(ctx.ps.id);
    } catch (err) {
      error(err, finalizar ? "No se pudo finalizar la rutina" : "No se pudo anular la rutina");
    }
  };

  // ---------------- edición de líneas ----------------

  const actualizarLinea = (key: string, cambio: (l: LineaEdit) => LineaEdit) =>
    setForm((f) => (f ? { ...f, lineas: f.lineas.map((l) => (l.key === key ? cambio(l) : l)) } : f));

  /** Una línea puede tener varias tareas (o varias profundas) del mismo sector. */
  const toggleTarea = (key: string, tareaId: number, profunda: boolean) =>
    actualizarLinea(key, (l) => {
      const tareas = { ...l.tareas };
      if (tareas[tareaId]) delete tareas[tareaId];
      else
        tareas[tareaId] = profunda
          ? { frecuencia: "MENSUAL", dias_semana: [], semanas_mes: [] }
          : { frecuencia: "DIARIA", dias_semana: [], semanas_mes: [] };
      return { ...l, tareas };
    });

  /** Checks excluyentes "Agregar tarea" / "Limpieza profunda". Al cambiar de
   *  tipo se quitan las tareas del otro (pide confirmación si había cargadas).
   *  Una línea de profunda aplica a todos los días del grupo: sus días salen de
   *  cada limpieza profunda. */
  const elegirTipo = (l: LineaEdit, tipo: TipoLinea, activo: boolean) => {
    if (!activo) {
      if (l.tipo !== tipo) return;
      const aplicar = () => actualizarLinea(l.key, (x) => ({ ...x, tipo: "", tareas: {} }));
      if (!Object.keys(l.tareas).length) return aplicar();
      return presentAlert({
        header: "Quitar selección",
        message: `Se quitan ${tipo === "PROFUNDA" ? "las limpiezas profundas" : "las tareas"} marcadas en esta línea.`,
        buttons: [
          { text: "Cancelar", role: "cancel" },
          { text: "Quitar", handler: () => { aplicar(); } },
        ],
      });
    }
    if (l.tipo === tipo) return;
    const grupo = grupos.find((g) => g.key === l.grupo);
    const aplicar = () =>
      cambiarConControl(l.key, (x) => ({
        ...x,
        tipo,
        tareas: {},
        dias: tipo === "PROFUNDA" && grupo ? [...grupo.dias] : x.dias,
      }));
    if (!Object.keys(l.tareas).length) return aplicar();
    presentAlert({
      header: tipo === "PROFUNDA" ? "Cambiar a limpieza profunda" : "Cambiar a tareas",
      message: "Un horario no puede tener tareas y limpieza profunda. Se quitan las que ya marcaste en esta línea.",
      buttons: [
        { text: "Cancelar", role: "cancel" },
        { text: "Cambiar", handler: () => { aplicar(); } },
      ],
    });
  };

  const configTarea = (key: string, tareaId: number, cambio: Partial<ConfigTarea>) =>
    cambiarConControl(key, (l) => ({ ...l, tareas: { ...l.tareas, [tareaId]: { ...l.tareas[tareaId], ...cambio } } }));

  /**
   * Aplica un cambio a una línea controlando que no choque con otra en el mismo
   * día y horario. Si choca, no se aplica: se avisa qué línea ocupa ese horario
   * y que hay que eliminarla para cargar la nueva.
   */
  const cambiarConControl = (key: string, cambio: (l: LineaEdit) => LineaEdit) => {
    if (!form) return;
    const actual = form.lineas.find((l) => l.key === key);
    if (!actual) return;
    const nueva = cambio(actual);
    if (JSON.stringify(nueva) === JSON.stringify(actual)) return;
    const choques = choquesDe(nueva, form.lineas, grupos);
    if (!choques.length) return actualizarLinea(key, () => nueva);
    const detalle = choques
      .map(({ linea, dias }) => {
        const tareas = Object.keys(linea.tareas).map((id) => nombreTarea(Number(id))).join(", ");
        return `• ${etiquetaDias(dias)} de ${linea.desde} a ${linea.hasta}: ${linea.nombre || "sin sector"}${tareas ? ` (${tareas})` : ""}`;
      })
      .join("<br>");
    presentAlert({
      header: "Ya hay una línea en ese horario",
      // Al cerrar se restauran los campos editados (el cambio no se aplica).
      backdropDismiss: false,
      message: `${detalle}<br><br>Si querés cargar esta línea en ese día y horario, primero eliminá la anterior.`,
      buttons: [{ text: "Entendido", role: "cancel", handler: () => setRevLinea((r) => r + 1) }],
    });
  };

  /** Nueva línea del grupo activo a continuación de la anterior, dentro de la
   *  asignación del operario (ver siguienteFranja). */
  const agregarLinea = (copiaDe?: LineaEdit) => {
    if (!form || !grupoActivo) return;
    const delGrupo = form.lineas.filter((l) => l.grupo === grupoActivo.key);
    const franja = siguienteFranja(grupoActivo.cortes, delGrupo);
    if (!franja) {
      const fin = grupoActivo.cortes[grupoActivo.cortes.length - 1]?.[1];
      aviso(`Ya está cubierto todo el horario de ${grupoActivo.etiqueta}${fin ? ` (hasta ${fin})` : ""}. Ajustá los horarios de la línea nueva.`);
    }
    setForm((f) => {
      if (!f || !grupoActivo) return f;
      const ultima = delGrupo.map((l) => l.hasta).filter(Boolean).sort().pop() || grupoActivo.cortes[0]?.[0] || "07:00";
      const base = lineaInicial(grupoActivo, franja || { desde: ultima, hasta: sumarMinutos(ultima, DURACION_LINEA) });
      const nueva: LineaEdit = copiaDe
        ? { ...JSON.parse(JSON.stringify(copiaDe)), key: base.key, grupo: base.grupo, desde: base.desde, hasta: base.hasta }
        : base;
      return { ...f, lineas: [...f.lineas, nueva] };
    });
  };

  /** Reemplaza la secuencia del grupo activo por una copia de la de otro grupo. */
  const copiarDeGrupo = (origen: GrupoDias) => {
    if (!grupoActivo) return;
    const aplicar = () =>
      setForm((f) => {
        if (!f) return f;
        const copias = f.lineas
          .filter((l) => l.grupo === origen.key)
          .map((l) => copiarAGrupo(l, origen, grupoActivo));
        return { ...f, lineas: [...f.lineas.filter((l) => l.grupo !== grupoActivo.key), ...copias] };
      });
    presentAlert({
      header: `Copiar de ${origen.etiqueta}`,
      message: `Se reemplaza la secuencia de ${grupoActivo.etiqueta} por la de ${origen.etiqueta}. Después ajustá lo que no entre en el horario y elegí el día de las limpiezas profundas que no coincidan.`,
      buttons: [
        { text: "Cancelar", role: "cancel" },
        { text: "Copiar", handler: () => { aplicar(); } },
      ],
    });
  };

  const quitarLinea = (key: string) =>
    setForm((f) => (f ? { ...f, lineas: f.lineas.filter((l) => l.key !== key) } : f));

  /** Días de una línea de tareas: siempre al menos uno. Si en esos días y
   *  horario ya hay otra línea, no se aplica y se avisa. */
  const toggleDiaLinea = (key: string, dia: number) =>
    cambiarConControl(key, (l) => {
      const dias = toggle(l.dias, dia);
      if (!dias.length) return l;
      return { ...conDias(l, dias, l.grupo), key: l.key };
    });

  // ---------------- resumen y confirmación ----------------

  /** Validación previa al resumen (el backend vuelve a validar al guardar). */
  const validar = (): string | null => {
    if (!form || !operario) return "Seleccioná el operario";
    if (!form.lineas.some((l) => !lineaVacia(l))) return "Cargá al menos una línea";
    // Carga progresiva: se puede guardar una parte del horario; lo que falta se
    // ve en los indicadores de cobertura y se completa después con Modificar.
    for (const g of grupos) {
      const delGrupo = form.lineas.filter((l) => l.grupo === g.key && !lineaVacia(l));
      const cortes = g.cortes;
      for (let i = 0; i < delGrupo.length; i++) {
        const l = delGrupo[i];
        const n = `${grupos.length > 1 ? `${g.etiqueta}, línea` : "Línea"} ${i + 1}${l.nombre ? ` (${l.nombre})` : ""}`;
        if (!l.desde || !l.hasta) return `${n}: indicá el horario desde/hasta`;
        if (!l.nombre.trim()) return `${n}: indicá el sector`;
        if (!l.dias.length) return `${n}: elegí al menos un día`;
        if (cortes.length && !dentroDeCortes(l.desde, l.hasta, cortes))
          return `${n}: ${l.desde}-${l.hasta} está fuera del horario del operario`;
        if (!cortes.length && l.desde >= l.hasta) return `${n}: el horario es inválido`;
        const vigentes = idsCatalogo();
        const ids = Object.keys(l.tareas).map(Number).filter((id) => vigentes.has(id));
        if (!l.tipo) return `${n}: marcá «Agregar tarea» o «Limpieza profunda»`;
        if (!ids.length)
          return `${n}: marcá al menos ${l.tipo === "PROFUNDA" ? "una limpieza profunda" : "una tarea"}`;
        const choque = choquesDe(l, form.lineas, grupos)[0];
        if (choque)
          return `${n}: ${etiquetaDias(choque.dias)} de ${choque.linea.desde} a ${choque.linea.hasta} ya tiene "${choque.linea.nombre}"`;
        for (const id of ids.filter(esProfunda)) {
          const c = l.tareas[id];
          const t = nombreTarea(id);
          if (!c.dias_semana.length) return `${n}: elegí el día de "${t}"`;
          if (c.frecuencia === "QUINCENAL" && c.semanas_mes.length !== 2) return `${n}: elegí las semanas de "${t}"`;
          if (c.frecuencia === "MENSUAL" && c.semanas_mes.length !== 1) return `${n}: elegí la semana de "${t}"`;
        }
      }
    }
    return null;
  };

  const irAResumen = () => {
    const err = validar();
    if (err) return aviso(err);
    // El resumen y lo que se guarda van por grupo y en orden de ejecución; las
    // líneas agregadas y no completadas se descartan.
    setForm((f) => (f ? { ...f, lineas: ordenarLineas(f.lineas.filter((l) => !lineaVacia(l)), grupos) } : f));
    setVista("resumen");
  };

  const payload = (): RutinaPayload => ({
    baltic_nro: form?.baltic_nro || "",
    observaciones: form?.observaciones || "",
    sectores: (form?.lineas || []).filter((l) => !lineaVacia(l)).map((l) => ({
      nombre: l.nombre.trim(),
      hora_desde: l.desde,
      hora_hasta: l.hasta,
      dias_semana: l.dias,
      // Las diarias solo llevan el id; las profundas, su frecuencia, días y semanas.
      tareas: Object.entries(l.tareas)
        .filter(([id]) => (l.tipo === "PROFUNDA") === esProfunda(Number(id)))
        .map(([id, c]) =>
          l.tipo === "PROFUNDA"
            ? { tarea_id: Number(id), frecuencia: c.frecuencia, dias_semana: c.dias_semana, semanas_mes: c.semanas_mes }
            : { tarea_id: Number(id) }
        ),
    })),
    asignaciones: operario ? idsDe(operario) : [],
  });

  const confirmar = async () => {
    if (!ctx || !operario || guardando) return;
    setGuardando(true);
    try {
      if (rutina) await RutinaAPI.modificar(rutina.rutina_id, payload());
      else await RutinaAPI.crear({ ps_id: ctx.ps.id, ...payload() });
      setToast({ show: true, msg: `Rutina de ${operario.nombre} guardada`, color: "success" });
      // Ya guardada: se sale del resumen antes de recargar, así un error al
      // recargar no deja "Confirmar" disponible para guardarla dos veces.
      setForm(null);
      setRutina(null);
      setVista("ps");
      await abrirPS(ctx.ps.id);
    } catch (err) {
      error(err, "No se pudo guardar la rutina");
    } finally {
      setGuardando(false);
    }
  };

  // ---------------- navegación ----------------

  const volver = () => {
    if (vista === "resumen") {
      setVista("editor");
    } else if (vista === "editor") {
      presentAlert({
        header: "Salir sin guardar",
        message: "Lo cargado no se guarda.",
        buttons: [
          { text: "Seguir editando", role: "cancel" },
          { text: "Salir", handler: () => setVista("ps") },
        ],
      });
    } else if (vista === "ver") {
      setVista("ps");
    } else if (vista === "ps" && esJefe) {
      setVista("panel");
      setCtx(null);
      cargarPanel(true);
    } else {
      setVista("buscar");
      setCtx(null);
    }
  };

  const vistaInicial: Vista = esJefe ? "panel" : "buscar";

  // ---------------- render ----------------

  /** Horario del operario por día. Con `lineas`, cada tramo muestra si ya está
   *  cubierto por la rutina, a medias o si falta (carga progresiva). */
  const renderHorario = (op: OperarioPS, lineas?: LineaCobertura[]) => {
    const cobertura = lineas ? coberturaHorario(op, lineas) : null;
    const estados = cobertura ? Object.values(cobertura).flatMap((d) => Object.values(d)) : [];
    return (
      <div style={{ marginTop: 4 }}>
        {horarioPorDia(op).map((d) => {
          const dia = DIAS_SEMANA.find((x) => x.clave === d.dia)?.valor || 0;
          return (
            <IonChip key={d.dia} outline style={{ height: "auto", minHeight: 24, fontSize: 12, margin: "2px 4px 2px 0" }}>
              <strong style={{ textTransform: "capitalize", marginRight: 4 }}>{d.dia}</strong>
              {d.tramos.map((t, i) => {
                const estado = cobertura?.[dia]?.[t];
                const info = estado ? ICONO_TRAMO[estado] : null;
                return (
                  <span key={t} style={{ display: "inline-flex", alignItems: "center", marginLeft: i ? 6 : 0 }}>
                    {info && (
                      <IonIcon icon={info.icono} style={{ color: info.color, fontSize: 15, marginRight: 2 }} title={info.texto} />
                    )}
                    {t}
                  </span>
                );
              })}
            </IonChip>
          );
        })}
        {op.horario.length === 0 && <IonText color="medium">Sin horario cargado</IonText>}
        {cobertura && estados.length > 0 && vista !== "resumen" && (
          <div style={{ fontSize: 12, marginTop: 4, color: "#555" }}>
            {(["COMPLETO", "PARCIAL", "PENDIENTE"] as EstadoTramo[]).map((e) => (
              <span key={e} style={{ marginRight: 10, whiteSpace: "nowrap" }}>
                <IonIcon icon={ICONO_TRAMO[e].icono} style={{ color: ICONO_TRAMO[e].color, verticalAlign: "-2px" }} />{" "}
                {ICONO_TRAMO[e].texto}
              </span>
            ))}
            {estados.every((e) => e === "COMPLETO") ? (
              <strong style={{ color: "#2e7d32" }}>Horario completo</strong>
            ) : (
              <span>
                {vista === "ver"
                  ? "Faltan franjas: tocá «Modificar / completar»."
                  : vista === "editor"
                  ? "Podés guardar y completar el resto más tarde."
                  : ""}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  /** Panel del Jefe de Operaciones: avance por fiscal y PS. */
  const renderPanel = () => {
    if (errorPanel && !panel)
      return (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <IonText color="medium">
            <p>No se pudo cargar el avance de las rutinas.</p>
          </IonText>
          <IonButton onClick={() => cargarPanel()}>Reintentar</IonButton>
        </div>
      );
    if (!panel)
      return (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <IonSpinner name="crescent" />
        </div>
      );
    const f = normalizar(filtroPanel);
    const visibles = panel.filter(
      (p) => !f || normalizar(p.ps_nombre).includes(f) || normalizar(p.fiscal).includes(f) || normalizar(p.ps_codigo).includes(f)
    );
    const total = (campo: keyof ResumenPS) => visibles.reduce((n, p) => n + Number(p[campo] || 0), 0);
    const fiscales = Array.from(new Set(visibles.map((p) => p.fiscal)));
    const kpis: [string, number, string][] = [
      ["Operarios", total("operarios"), "#333"],
      ["Con rutina", total("con_rutina"), GARNET],
      ["Completas", total("completas"), "#2e7d32"],
      ["Carga parcial", total("parciales"), "#ef6c00"],
      ["Desactualizadas", total("desactualizadas"), "#c62828"],
    ];
    return (
      <>
        <h2 style={{ margin: 0 }}>Avance de rutinas</h2>
        <IonText color="medium">
          <small>Puntos de servicio de tus fiscales. Tocá uno para ver o modificar las rutinas de sus operarios.</small>
        </IonText>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "10px 0" }}>
          {kpis.map(([etiqueta, valor, color]) => (
            <div key={etiqueta} style={{ flex: "1 1 90px", background: "#f7f7f9", borderRadius: 8, padding: "8px 10px" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color }}>{valor}</div>
              <div style={{ fontSize: 11, color: "#666" }}>{etiqueta}</div>
            </div>
          ))}
        </div>
        <IonSearchbar
          value={filtroPanel}
          placeholder="Buscar PS o fiscal"
          debounce={200}
          onIonChange={(e) => setFiltroPanel(e.detail.value || "")}
          style={{ padding: "0 0 4px" }}
        />
        {fiscales.map((fiscal) => {
          const delFiscal = visibles.filter((p) => p.fiscal === fiscal);
          const ops = delFiscal.reduce((n, p) => n + p.operarios, 0);
          const completas = delFiscal.reduce((n, p) => n + p.completas, 0);
          return (
            <div key={fiscal} style={{ marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <strong style={{ color: GARNET }}>{fiscal}</strong>
                <IonText color="medium">
                  <small>
                    {completas}/{ops} completas
                  </small>
                </IonText>
              </div>
              <IonList style={{ paddingTop: 0 }}>
                {delFiscal.map((p) => {
                  const avance = p.operarios ? Math.round((100 * p.completas) / p.operarios) : 0;
                  return (
                    <IonItem key={p.id} button detail onClick={() => abrirPS(p.id)}>
                      <IonLabel className="ion-text-wrap">
                        <h3>{p.ps_nombre}</h3>
                        <p>
                          {p.con_rutina} de {p.operarios} operarios con rutina · {p.completas} completas
                        </p>
                        <div style={{ height: 6, background: "#eee", borderRadius: 4, margin: "4px 0" }}>
                          <div style={{ width: `${avance}%`, height: 6, background: "#2e7d32", borderRadius: 4 }} />
                        </div>
                        <div>
                          {p.parciales > 0 && <IonBadge color="warning">{p.parciales} carga parcial</IonBadge>}{" "}
                          {p.desactualizadas > 0 && <IonBadge color="danger">{p.desactualizadas} desactualizada(s)</IonBadge>}{" "}
                          {p.operarios - p.con_rutina > 0 && (
                            <IonBadge color="medium">{p.operarios - p.con_rutina} sin rutina</IonBadge>
                          )}
                        </div>
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </IonList>
            </div>
          );
        })}
        {visibles.length === 0 && (
          <IonText color="medium">
            <p>{panel.length ? "Ningún PS coincide con la búsqueda." : "No tenés puntos de servicio asignados a tus fiscales."}</p>
          </IonText>
        )}
      </>
    );
  };

  const renderBuscar = () => (
    <>
      <IonText>
        <p style={{ marginTop: 0 }}>Elegí el punto de servicio para ver o armar las rutinas de sus operarios.</p>
      </IonText>
      <IonItem>
        <IonInput
          value={q}
          placeholder="Buscar punto de servicio"
          onIonChange={(e) => onBuscar(e.detail.value || "")}
          clearInput
        />
      </IonItem>
      {buscando ? (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <IonSpinner name="crescent" />
        </div>
      ) : (
        <IonList>
          {puntos.map((p) => (
            <IonItem key={p.id} button detail onClick={() => abrirPS(p.id)}>
              <IonLabel>
                <h3>{p.ps_nombre}</h3>
                <p>{p.ps_codigo}</p>
              </IonLabel>
            </IonItem>
          ))}
          {puntos.length === 0 && (
            <IonText color="medium">
              <p>
                {q.trim().length < MIN_CARACTERES_BUSQUEDA
                  ? `Escribí al menos ${MIN_CARACTERES_BUSQUEDA} letras del nombre del punto de servicio.`
                  : "No se encontraron puntos de servicio asignados con ese nombre."}
              </p>
            </IonText>
          )}
        </IonList>
      )}
    </>
  );

  const renderPS = () => {
    if (!ctx) return null;
    const visibles = filtrarOperarios(ctx.operarios, filtroOp);
    const rutinasConOperario = new Set(ctx.operarios.filter((o) => o.rutina_vigente).map((o) => o.rutina_vigente!.rutina_id));
    const huerfanas = ctx.rutinas.filter((r) => !rutinasConOperario.has(r.rutina_id));
    const conRutina = ctx.operarios.filter((o) => o.rutina_vigente).length;
    return (
      <>
        <h2 style={{ margin: 0 }}>{ctx.ps.ps_nombre}</h2>
        <IonText color="medium">
          <small>
            {conRutina} de {ctx.operarios.length} operarios con rutina
          </small>
        </IonText>
        <p style={{ margin: "12px 0 4px" }}>Elegí el operario para armar o ver su rutina.</p>

        {ctx.operarios.length >= MIN_OPERARIOS_BUSCADOR && (
          <IonSearchbar
            value={filtroOp}
            placeholder="Buscar operario por nombre o legajo"
            debounce={200}
            onIonChange={(e) => setFiltroOp(e.detail.value || "")}
            style={{ padding: "0 0 4px" }}
          />
        )}

        {visibles.map((o) => (
          <IonCard key={o.legajo} style={{ margin: "8px 0" }}>
            <IonCardContent>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div>
                  <strong style={{ fontSize: 16, color: "#222" }}>{o.nombre}</strong>
                  <div>
                    <IonText color="medium">
                      <small>Legajo {o.legajo}</small>
                    </IonText>
                  </div>
                </div>
                {o.rutina_vigente ? (
                  <div style={{ textAlign: "right" }}>
                    <IonBadge color={o.cobertura === "PARCIAL" || o.cobertura === "SIN_CARGAR" ? "warning" : "success"}>
                      Rutina N° {o.rutina_vigente.numero}
                    </IonBadge>
                    {(o.cobertura === "PARCIAL" || o.cobertura === "SIN_CARGAR") && (
                      <div>
                        <IonBadge color="warning" style={{ marginTop: 4 }}>Carga parcial</IonBadge>
                      </div>
                    )}
                    {o.rutina_desactualizada && (
                      <div>
                        <IonBadge color="danger" style={{ marginTop: 4 }}>Desactualizada con el horario</IonBadge>
                      </div>
                    )}
                  </div>
                ) : (
                  <IonBadge color="warning">Sin rutina</IonBadge>
                )}
              </div>
              {renderHorario(o)}
              <div style={{ marginTop: 6 }}>
                {o.rutina_vigente ? (
                  <>
                    <IonButton size="small" fill="outline" onClick={() => abrirRutina(o.rutina_vigente!.rutina_id, o, "ver")}>
                      <IonIcon slot="start" icon={eyeOutline} /> Ver
                    </IonButton>
                    <IonButton size="small" onClick={() => abrirRutina(o.rutina_vigente!.rutina_id, o, "editor")}>
                      <IonIcon slot="start" icon={createOutline} /> Modificar
                    </IonButton>
                  </>
                ) : (
                  <IonButton size="small" onClick={() => armarRutina(o)}>
                    <IonIcon slot="start" icon={addOutline} /> Armar rutina
                  </IonButton>
                )}
              </div>
            </IonCardContent>
          </IonCard>
        ))}
        {ctx.operarios.length === 0 && (
          <IonText color="medium">
            <p>No hay operarios con asignación activa en este punto de servicio.</p>
          </IonText>
        )}
        {filtroOp && visibles.length === 0 && (
          <IonText color="medium">
            <p>Ningún operario coincide con "{filtroOp}".</p>
          </IonText>
        )}

        {huerfanas.length > 0 && (
          <>
            <h3>Rutinas sin operario activo</h3>
            {huerfanas.map((r) => (
              <IonItem key={r.rutina_id} button detail onClick={() => abrirRutina(r.rutina_id, null, "ver")}>
                <IonLabel>
                  <h3>{numeroRutina(r)}</h3>
                  <p>{r.operarios.join(", ") || "Sin operarios"}</p>
                </IonLabel>
              </IonItem>
            ))}
          </>
        )}
      </>
    );
  };

  const renderConfigProfunda = (l: LineaEdit, tareaId: number) => {
    const c = l.tareas[tareaId];
    const diasLinea = DIAS_SEMANA.filter((d) => l.dias.includes(d.valor));
    return (
      <div key={tareaId} style={{ background: "#fbf3f5", borderRadius: 8, padding: 8, marginTop: 6 }}>
        <strong style={{ fontSize: 13 }}>{nombreTarea(tareaId)}</strong>
        <IonSegment
          value={c.frecuencia}
          onIonChange={(e) => {
            const frecuencia = (e.detail.value as Frecuencia) || "MENSUAL";
            if (frecuencia !== c.frecuencia) configTarea(l.key, tareaId, { frecuencia, semanas_mes: [] });
          }}
        >
          <IonSegmentButton value="SEMANAL">
            <IonLabel>Semanal</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="QUINCENAL">
            <IonLabel>Quincenal</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="MENSUAL">
            <IonLabel>Mensual</IonLabel>
          </IonSegmentButton>
        </IonSegment>
        {c.frecuencia === "QUINCENAL" && (
          <div style={{ marginTop: 4 }}>
            {[[1, 3], [2, 4]].map((par) => (
              <IonChip
                key={par.join()}
                color={c.semanas_mes.join() === par.join() ? "primary" : "medium"}
                outline={c.semanas_mes.join() !== par.join()}
                onClick={() => configTarea(l.key, tareaId, { semanas_mes: par })}
              >
                {par[0]}ª y {par[1]}ª semana
              </IonChip>
            ))}
          </div>
        )}
        {c.frecuencia === "MENSUAL" && (
          <div style={{ marginTop: 4 }}>
            {[1, 2, 3, 4].map((sem) => (
              <IonChip
                key={sem}
                color={c.semanas_mes[0] === sem ? "primary" : "medium"}
                outline={c.semanas_mes[0] !== sem}
                onClick={() => configTarea(l.key, tareaId, { semanas_mes: [sem] })}
              >
                {sem}ª sem.
              </IonChip>
            ))}
          </div>
        )}
        <div style={{ marginTop: 2 }}>
          {diasLinea.map((d) => (
            <IonChip
              key={d.valor}
              color={c.dias_semana.includes(d.valor) ? "primary" : "medium"}
              outline={!c.dias_semana.includes(d.valor)}
              onClick={() => configTarea(l.key, tareaId, { dias_semana: toggle(c.dias_semana, d.valor) })}
            >
              {d.corto}
            </IonChip>
          ))}
        </div>
      </div>
    );
  };

  const renderLinea = (l: LineaEdit, idx: number) => {
    if (!catalogo || !operario) return null;
    const grupo = grupos.find((g) => g.key === l.grupo);
    const cortes = grupo?.cortes || [];
    const fuera = !!(l.desde && l.hasta && cortes.length && !dentroDeCortes(l.desde, l.hasta, cortes));
    return (
      <IonCard key={l.key} style={{ margin: "10px 0", borderLeft: `4px solid ${GARNET}` }}>
        <IonCardContent>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong style={{ color: GARNET }}>Línea {idx + 1}</strong>
            <div>
              <IonButton fill="clear" size="small" title="Duplicar línea" onClick={() => agregarLinea(l)}>
                <IonIcon icon={copyOutline} />
              </IonButton>
              <IonButton fill="clear" size="small" color="danger" title="Quitar línea" onClick={() => quitarLinea(l.key)}>
                <IonIcon icon={trashOutline} />
              </IonButton>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <IonItem lines="full" style={{ flex: 1 }}>
              <IonLabel position="stacked">Desde</IonLabel>
              <IonInput
                key={`d-${l.key}-${revLinea}`}
                type="time"
                value={l.desde}
                onIonChange={(e) => {
                  const v = e.detail.value || "";
                  if (v !== l.desde) cambiarConControl(l.key, (x) => ({ ...x, desde: v }));
                }}
              />
            </IonItem>
            <IonItem lines="full" style={{ flex: 1 }}>
              <IonLabel position="stacked">Hasta</IonLabel>
              <IonInput
                key={`h-${l.key}-${revLinea}`}
                type="time"
                value={l.hasta}
                onIonChange={(e) => {
                  const v = e.detail.value || "";
                  if (v !== l.hasta) cambiarConControl(l.key, (x) => ({ ...x, hasta: v }));
                }}
              />
            </IonItem>
          </div>
          {fuera && (
            <IonText color="danger">
              <small>Fuera del horario de {etiquetaDias(l.dias)} ({cortes.map((c) => c.join("-")).join(" / ")})</small>
            </IonText>
          )}
          <IonItem lines="full">
            <IonLabel position="stacked">Sector</IonLabel>
            <IonInput
              value={l.nombre}
              placeholder="Ej.: Recepción, Baños PB"
              maxlength={200}
              onIonChange={(e) => actualizarLinea(l.key, (x) => ({ ...x, nombre: e.detail.value || "" }))}
            />
          </IonItem>

          {/* Checks excluyentes: un horario es de tareas o de limpieza profunda. */}
          {/* Opciones apilables: en pantallas angostas cada una ocupa todo el ancho. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "10px 0 8px" }}>
            {([
              ["TAREAS", "Agregar tarea"],
              ["PROFUNDA", "Limpieza profunda"],
            ] as [TipoLinea, string][]).map(([tipo, texto]) => {
              const activo = l.tipo === tipo;
              return (
                <div
                  key={tipo}
                  role="checkbox"
                  aria-checked={activo}
                  tabIndex={0}
                  onClick={() => elegirTipo(l, tipo, !activo)}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      elegirTipo(l, tipo, !activo);
                    }
                  }}
                  style={{
                    flex: "1 1 150px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                    border: `1.5px solid ${activo ? GARNET : "#d0d0d0"}`,
                    background: activo ? "#fbf3f5" : "#fff",
                    color: activo ? GARNET : "#333",
                    fontWeight: activo ? 700 : 500,
                  }}
                >
                  <IonIcon icon={activo ? checkboxOutline : squareOutline} style={{ fontSize: 22, flexShrink: 0 }} />
                  <span style={{ whiteSpace: "normal", lineHeight: 1.2 }}>{texto}</span>
                </div>
              );
            })}
          </div>

          {l.tipo === "TAREAS" && (
            <div style={{ background: "#f7f7f9", borderRadius: 8, padding: 8 }}>
              {grupo && grupo.dias.length > 1 && (
                <>
                  <IonText color="medium">
                    <small>
                      Días{l.dias.length < grupo.dias.length ? ` — solo ${etiquetaDias(l.dias)}` : " — todos los del grupo"}
                    </small>
                  </IonText>
                  <div>
                    {DIAS_SEMANA.filter((d) => grupo.dias.includes(d.valor)).map((d) => (
                      <IonChip
                        key={d.valor}
                        color={l.dias.includes(d.valor) ? "primary" : "medium"}
                        outline={!l.dias.includes(d.valor)}
                        onClick={() => toggleDiaLinea(l.key, d.valor)}
                        style={{ height: 26 }}
                      >
                        {d.corto}
                      </IonChip>
                    ))}
                  </div>
                </>
              )}
              <IonText color="medium">
                <small>Tareas</small>
              </IonText>
              <div>
                {catalogo.diarias.map((t) => (
                  <IonChip
                    key={t.id}
                    color={l.tareas[t.id] ? "primary" : "medium"}
                    outline={!l.tareas[t.id]}
                    onClick={() => toggleTarea(l.key, t.id, false)}
                  >
                    {t.nombre}
                  </IonChip>
                ))}
              </div>
            </div>
          )}

          {l.tipo === "PROFUNDA" && (
            <div style={{ background: "#f7f7f9", borderRadius: 8, padding: 8 }}>
              <IonText color="medium">
                <small>Limpiezas profundas (cada una con su frecuencia, semanas y días)</small>
              </IonText>
              <div>
                {catalogo.profundas.map((t) => (
                  <IonChip
                    key={t.id}
                    color={l.tareas[t.id] ? "primary" : "medium"}
                    outline={!l.tareas[t.id]}
                    onClick={() => toggleTarea(l.key, t.id, true)}
                  >
                    {t.nombre}
                  </IonChip>
                ))}
              </div>
              {catalogo.profundas.filter((t) => l.tareas[t.id]).map((t) => renderConfigProfunda(l, t.id))}
            </div>
          )}
        </IonCardContent>
      </IonCard>
    );
  };

  const renderEncabezadoOperario = () =>
    operario && (
      <IonCard style={{ margin: "0 0 10px", background: "#fbf3f5" }}>
        <IonCardContent>
          <IonText color="medium">
            <small>{ctx?.ps.ps_nombre}</small>
          </IonText>
          <h2 style={{ margin: "2px 0 0", fontWeight: 700, color: GARNET }}>{operario.nombre}</h2>
          <IonText color="medium">
            <small>
              Legajo {operario.legajo}
              {vista === "ver" ? "" : rutina ? ` · modificando ${numeroRutina(rutina)}` : " · rutina nueva"}
            </small>
          </IonText>
          {renderHorario(
            operario,
            vista === "ver" && rutina
              ? rutina.sectores.map((s) => ({
                  desde: s.hora_desde || "",
                  hasta: s.hora_hasta || "",
                  dias:
                    s.tipo === "PROFUNDA"
                      ? Array.from(new Set(s.tareas.flatMap((t) => t.dias_semana)))
                      : s.dias_semana || [],
                }))
              : lineasCobertura(form?.lineas || [], grupos)
          )}
        </IonCardContent>
      </IonCard>
    );

  const renderEditor = () => {
    if (!form || !ctx || !operario) return null;
    const otras = ctx.rutinas.filter((r) => r.rutina_id !== rutina?.rutina_id);
    return (
      <>
        {renderEncabezadoOperario()}

        {otras.length > 0 && (
          <IonItem>
            <IonIcon icon={copyOutline} slot="start" color="medium" />
            <IonLabel>Copiar de otra rutina</IonLabel>
            <IonSelect
              key={`copiar-${revCopiar}`}
              interface="action-sheet"
              placeholder="Elegir"
              value={null}
              onIonChange={(e) => e.detail.value && copiarDe(Number(e.detail.value))}
            >
              {otras.map((r) => (
                <IonSelectOption key={r.rutina_id} value={r.rutina_id}>
                  {r.operarios.join(", ") || numeroRutina(r)}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
        )}

        {renderBaltic()}

        <h3 style={{ marginBottom: 0 }}>Secuencia de trabajo</h3>
        <IonText color="medium">
          <small>
            Cargá en orden qué hace, dónde y en qué horario. Las líneas se ordenan por horario al revisar.
            {grupos.length > 1 && " Hay una pestaña por cada horario distinto del operario."}
            {" Si algún día la rutina es distinta, desmarcá ese día en la línea y cargá otra línea para ese día en el mismo horario."}
          </small>
        </IonText>
        {grupoActivo && (
          <>
            {grupos.length > 1 && (
            <IonSegment
              scrollable
              value={grupoActivo.key}
              onIonChange={(e) => e.detail.value && setGrupoKey(e.detail.value)}
              style={{ marginTop: 8 }}
            >
              {grupos.map((g) => {
                const cant = form.lineas.filter((l) => l.grupo === g.key).length;
                return (
                  <IonSegmentButton key={g.key} value={g.key}>
                    <IonLabel>
                      {g.etiqueta}
                      <div style={{ fontSize: 11, textTransform: "none" }}>
                        {g.cortes.map((c) => c.join("-")).join(" / ")} · {cant} línea(s)
                      </div>
                    </IonLabel>
                  </IonSegmentButton>
                );
              })}
            </IonSegment>
            )}
            <div style={{ marginTop: 4 }}>
              {grupos
                .filter((g) => g.key !== grupoActivo.key && form.lineas.some((l) => l.grupo === g.key))
                .map((g) => (
                  <IonButton key={g.key} size="small" fill="clear" onClick={() => copiarDeGrupo(g)}>
                    <IonIcon slot="start" icon={copyOutline} /> Copiar de {g.etiqueta}
                  </IonButton>
                ))}
            </div>
          </>
        )}
        {grupoActivo &&
          form.lineas.filter((l) => l.grupo === grupoActivo.key).map(renderLinea)}
        <IonButton expand="block" fill="outline" onClick={() => agregarLinea()}>
          <IonIcon slot="start" icon={addOutline} /> Agregar línea{grupos.length > 1 && grupoActivo ? ` (${grupoActivo.etiqueta})` : ""}
        </IonButton>

        <IonItem style={{ marginTop: 12 }}>
          <IonLabel position="stacked">Observaciones</IonLabel>
          <IonTextarea
            value={form.observaciones}
            autoGrow
            maxlength={4000}
            placeholder="Ej.: reposición de insumos y check list en baños de alto tránsito"
            onIonChange={(e) => {
              const observaciones = e.detail.value || "";
              setForm((f) => (f ? { ...f, observaciones } : f));
            }}
          />
        </IonItem>

        <div style={{ display: "flex", gap: 8, margin: "16px 0 32px" }}>
          <IonButton style={{ flex: 1 }} fill="outline" color="medium" onClick={volver}>
            Cancelar
          </IonButton>
          <IonButton style={{ flex: 1 }} onClick={irAResumen}>
            <IonIcon slot="start" icon={listOutline} /> Revisar
          </IonButton>
        </div>
      </>
    );
  };

  const renderBaltic = () => {
    if (!ctx || !form) return null;
    if (ctx.baltics.length > 1)
      return (
        <IonItem>
          <IonLabel position="stacked">Relevamiento Baltic vigente</IonLabel>
          <IonSelect
            value={form.baltic_nro}
            interface="action-sheet"
            onIonChange={(e) => {
              const baltic_nro = e.detail.value || "";
              setForm((f) => (f ? { ...f, baltic_nro } : f));
            }}
          >
            {ctx.baltics.map((b) => (
              <IonSelectOption key={b.relevamiento} value={b.relevamiento}>
                {b.relevamiento}
                {b.rutinas.length ? ` · Rutina ${b.rutinas.join(", ")}` : ""}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
      );
    const actual = ctx.baltics.find((b) => b.relevamiento === form.baltic_nro);
    return (
      <IonItem lines="full">
        <IonLabel className="ion-text-wrap">
          <p>Relevamiento Baltic vigente</p>
          <h3>{form.baltic_nro || "Sin relevamiento vigente en Baltic"}</h3>
          {actual?.rutinas.length ? <p>Rutina Baltic: {actual.rutinas.join(", ")}</p> : null}
        </IonLabel>
      </IonItem>
    );
  };

  const renderResumen = () => {
    if (!form || !operario) return null;
    const totalDiarias = form.lineas.reduce(
      (n, l) => n + (l.tipo === "TAREAS" ? Object.keys(l.tareas).filter((id) => !esProfunda(Number(id))).length : 0), 0);
    const totalProfundas = form.lineas.reduce(
      (n, l) => n + (l.tipo === "PROFUNDA" ? Object.keys(l.tareas).filter((id) => esProfunda(Number(id))).length : 0), 0);
    return (
      <>
        <h2 style={{ margin: "0 0 8px" }}>Resumen de la rutina</h2>
        {renderEncabezadoOperario()}
        <IonText color="medium">
          <small>
            {form.lineas.length} línea(s) · {totalDiarias} tarea(s) · {totalProfundas} limpieza(s) profunda(s)
            {form.baltic_nro ? ` · Baltic ${form.baltic_nro}` : ""}
          </small>
        </IonText>

        {/* Por grupo de días: primero las tareas y después las limpiezas
            profundas, cada sección en orden de horario. */}
        <div style={{ marginTop: 10 }}>
          {grupos
            .filter((g) => form.lineas.some((l) => l.grupo === g.key))
            .map((g, gi) => {
              const delGrupo = form.lineas.filter((l) => l.grupo === g.key);
              const secciones = [
                { tipo: "TAREAS", titulo: "Tareas", icono: listOutline, lineas: delGrupo.filter((l) => l.tipo === "TAREAS") },
                {
                  tipo: "PROFUNDA",
                  titulo: "Limpiezas profundas",
                  icono: sparklesOutline,
                  lineas: delGrupo.filter((l) => l.tipo === "PROFUNDA"),
                },
              ].filter((x) => x.lineas.length);
              return (
                <div key={g.key} style={{ marginTop: gi ? 18 : 0 }}>
                  {grupos.length > 1 && (
                    <div style={{ background: "#fbf3f5", borderRadius: 6, padding: "6px 10px" }}>
                      <strong style={{ color: GARNET }}>{g.etiqueta}</strong>
                      <IonText color="medium"> · {g.cortes.map((c) => c.join("-")).join(" / ")}</IonText>
                    </div>
                  )}
                  {secciones.map((sec) => (
                    <div key={sec.tipo} style={{ marginTop: 10 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontWeight: 700,
                          color: sec.tipo === "PROFUNDA" ? "#4527a0" : GARNET,
                          borderBottom: `2px solid ${sec.tipo === "PROFUNDA" ? "#ede7f6" : "#f1e3e7"}`,
                          paddingBottom: 4,
                        }}
                      >
                        <IonIcon icon={sec.icono} /> {sec.titulo}
                        <IonText color="medium" style={{ fontWeight: 400, fontSize: 13 }}>
                          ({sec.lineas.length})
                        </IonText>
                      </div>
                      {sec.lineas.map((l) => {
                        const ids = Object.keys(l.tareas).map(Number);
                        const parcial = l.tipo === "TAREAS" && l.dias.length < g.dias.length;
                        return (
                          <div key={l.key} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: "1px solid #eee" }}>
                            <div style={{ minWidth: 92, color: GARNET, fontWeight: 700 }}>
                              <IonIcon icon={timeOutline} style={{ verticalAlign: "-2px" }} /> {l.desde}
                              <div style={{ fontWeight: 400, fontSize: 12, color: "#777", paddingLeft: 18 }}>a {l.hasta}</div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <strong>{l.nombre}</strong>
                              {parcial && (
                                <IonBadge color="tertiary" style={{ marginLeft: 6 }}>
                                  Solo {etiquetaDias(l.dias)}
                                </IonBadge>
                              )}
                              {l.tipo === "TAREAS" ? (
                                <div style={{ fontSize: 14 }}>{ids.filter((id) => !esProfunda(id)).map(nombreTarea).join(" · ")}</div>
                              ) : (
                                ids.filter(esProfunda).map((id) => (
                                  <div key={id} style={{ fontSize: 14 }}>
                                    {nombreTarea(id)}{" "}
                                    <IonText color="medium">
                                      <small>— {describirFrecuencia(l.tareas[id])}</small>
                                    </IonText>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })}
        </div>

        {form.observaciones && (
          <>
            <h3>Observaciones</h3>
            <p style={{ whiteSpace: "pre-wrap" }}>{form.observaciones}</p>
          </>
        )}

        {(() => {
          const estados = Object.values(coberturaHorario(operario, lineasCobertura(form.lineas, grupos))).flatMap((d) =>
            Object.values(d)
          );
          return estados.some((e) => e !== "COMPLETO") ? (
            <IonText color="warning">
              <p>
                <small>
                  La rutina todavía no cubre todo el horario del operario. Se guarda igual (el operario ve lo
                  cargado) y podés completarla más tarde con «Modificar».
                </small>
              </p>
            </IonText>
          ) : null;
        })()}
        {rutina && (
          <IonText color="medium">
            <p>
              <small>Se guarda como nueva versión y el operario deberá volver a aceptarla.</small>
            </p>
          </IonText>
        )}

        <div style={{ display: "flex", gap: 8, margin: "16px 0 32px" }}>
          <IonButton style={{ flex: 1 }} fill="outline" disabled={guardando} onClick={() => setVista("editor")}>
            <IonIcon slot="start" icon={createOutline} /> Modificar
          </IonButton>
          <IonButton style={{ flex: 1 }} disabled={guardando} onClick={confirmar}>
            <IonIcon slot="start" icon={checkmarkDoneOutline} /> Guardar rutina
          </IonButton>
        </div>
      </>
    );
  };

  const renderVer = () => {
    if (!rutina) return null;
    const aceptaron = new Set((rutina.aceptaciones || []).map((a) => a.legajo));
    const legajos = Array.from(new Set((rutina.asignaciones || []).map((a) => a.legajo)));
    return (
      <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, color: GARNET }}>{numeroRutina(rutina)}</h2>
          <EstadoRutinaChip estado={rutina.estado} />
        </div>
        <p style={{ margin: "4px 0" }}>{rutina.ps_nombre}</p>
        {operario && renderEncabezadoOperario()}
        <IonText color="medium">
          <small>
            {[
              rutina.inicio_en ? `Vigente desde ${fechaCorta(rutina.inicio_en)}` : "",
              rutina.baltic_nro ? `Baltic ${rutina.baltic_nro}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </small>
        </IonText>
        <h3>Secuencia de trabajo</h3>
        <RutinaVista sectores={rutina.sectores} />
        {rutina.observaciones && (
          <>
            <h3>Observaciones</h3>
            <p style={{ whiteSpace: "pre-wrap" }}>{rutina.observaciones}</p>
          </>
        )}
        {(rutina.versiones || []).length > 0 && (
          <>
            <h3>Versiones</h3>
            <IonList>
              {(rutina.versiones || []).map((v) => (
                <IonItem key={v.rutina_id} lines="full">
                  <IonLabel className="ion-text-wrap">
                    <h3>
                      Versión {v.version} <EstadoRutinaChip estado={v.estado} />
                    </h3>
                    <p>
                      Cargada por {v.emitido_nombre || "—"}
                      {v.publicada_en ? ` el ${fechaCorta(v.publicada_en)}` : ""}
                    </p>
                    {v.baja_en && (
                      <p>
                        {v.estado === "REEMPLAZADA" ? "Modificada" : v.estado === "FINALIZADA" ? "Finalizada" : "Anulada"} por{" "}
                        {v.baja_nombre || "—"} el {fechaCorta(v.baja_en)}
                        {v.baja_motivo ? ` · ${v.baja_motivo}` : ""}
                      </p>
                    )}
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          </>
        )}
        <h3>Operario</h3>
        <IonList>
          {legajos.map((legajo) => {
            const a = rutina.asignaciones!.find((x) => x.legajo === legajo)!;
            return (
              <IonItem key={legajo}>
                <IonLabel>{a.nombre}</IonLabel>
                <IonNote slot="end" color={aceptaron.has(legajo) ? "success" : "warning"}>
                  {aceptaron.has(legajo) ? "Aceptada" : "Pendiente de aceptar"}
                </IonNote>
              </IonItem>
            );
          })}
        </IonList>
        {rutina.estado === "VIGENTE" && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "16px 0 32px" }}>
            {operario && (
              <IonButton style={{ flex: "1 1 100%" }} onClick={() => irAEditor()}>
                <IonIcon slot="start" icon={createOutline} /> Modificar / completar
              </IonButton>
            )}
            <IonButton style={{ flex: 1 }} fill="outline" onClick={() => darDeBaja(rutina, "finalizar")}>
              <IonIcon slot="start" icon={flagOutline} /> Finalizar
            </IonButton>
            <IonButton style={{ flex: 1 }} fill="outline" color="medium" onClick={() => darDeBaja(rutina, "anular")}>
              <IonIcon slot="start" icon={trashOutline} /> Anular
            </IonButton>
          </div>
        )}
      </>
    );
  };

  return (
    <IonPage>
      <Header
        titulo="Rutinas"
        botonesDerecha={vista !== vistaInicial ? [{ icono: arrowBackOutline, action: volver }] : undefined}
      />
      <IonContent id="rutinas" className="ion-padding">
        {errorCatalogo && !catalogo ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <IonText color="medium">
              <p>No se pudo cargar el catálogo de tareas.</p>
            </IonText>
            <IonButton onClick={cargarCatalogo}>Reintentar</IonButton>
          </div>
        ) : cargando || !catalogo || !rolCargado ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <IonSpinner name="crescent" />
          </div>
        ) : vista === "panel" ? (
          renderPanel()
        ) : vista === "buscar" ? (
          renderBuscar()
        ) : vista === "ps" ? (
          renderPS()
        ) : vista === "editor" ? (
          renderEditor()
        ) : vista === "resumen" ? (
          renderResumen()
        ) : (
          renderVer()
        )}
        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={3500}
          onDidDismiss={() => setToast({ show: false, msg: "" })}
        />
      </IonContent>
    </IonPage>
  );
};

export default Rutinas;
